import { Image as ImageIcon, Loader2, Mic, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/lib/api';
import { summarizeSlide } from '@/lib/narration-context';
import type { Slide } from '@/lib/schema.types';
import { useEditorActions, useScript } from '@/store/editor.store';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Where to insert the new slide. If omitted, it's appended at the end. */
  insertAtIndex?: number;
}

type PhaseKey = 'thinking' | 'writing' | 'illustrating' | 'recording';

const PHASES: { key: PhaseKey; label: string; min: number }[] = [
  { key: 'thinking', label: 'Reading the lesson context…', min: 0 },
  { key: 'writing', label: 'Drafting the slide with Claude…', min: 1500 },
  { key: 'illustrating', label: 'Generating the illustration with Gemini…', min: 4500 },
  { key: 'recording', label: 'Recording narration for every step…', min: 9000 },
];

export function SuggestSlideDialog({ open, onClose, insertAtIndex }: Props) {
  const script = useScript();
  const actions = useEditorActions();

  const [hint, setHint] = useState('');
  const [includeImage, setIncludeImage] = useState(true);
  const [generateAudio, setGenerateAudio] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<PhaseKey>('thinking');
  const [elapsedMs, setElapsedMs] = useState(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);

  // Reset on open
  useEffect(() => {
    if (open) {
      setHint('');
      setError(null);
      setBusy(false);
      setPhase('thinking');
      setElapsedMs(0);
    }
  }, [open]);

  // Phase ticker — purely cosmetic, drives the loader copy.
  useEffect(() => {
    if (!busy) {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      return;
    }
    startedAtRef.current = Date.now();
    setPhase('thinking');
    setElapsedMs(0);
    tickRef.current = setInterval(() => {
      const dt = Date.now() - startedAtRef.current;
      setElapsedMs(dt);
      let next: PhaseKey = 'thinking';
      for (const p of PHASES) {
        if (p.key === 'illustrating' && !includeImage) continue;
        if (p.key === 'recording' && !generateAudio) continue;
        if (dt >= p.min) next = p.key;
      }
      setPhase(next);
    }, 250);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [busy, includeImage, generateAudio]);

  const generate = async () => {
    if (!script || busy) return;
    setBusy(true);
    setError(null);
    try {
      const beforeIndex =
        typeof insertAtIndex === 'number' ? insertAtIndex : script.slides.length;
      const contextSlides = script.slides.slice(0, beforeIndex).map(summarizeSlide);
      const res = await api.ai.suggestNextSlide({
        scriptLabel: script.label,
        metadata: script.metadata,
        existingSlides: contextSlides,
        userHint: hint.trim() || undefined,
        includeImage,
        generateAudio,
      });
      actions.insertSlide(res.slide as Slide, beforeIndex);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'AI suggest failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onClose}
      title="AI Suggest next slide"
      description="Claude will design the next slide based on what's already in your lesson."
      width="md"
    >
      {busy ? (
        <BusyView
          phase={phase}
          elapsedMs={elapsedMs}
          includeImage={includeImage}
          generateAudio={generateAudio}
        />
      ) : (
        <div className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-fg-muted">
              What should this slide cover? <span className="text-fg-subtle">(optional)</span>
            </span>
            <textarea
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              rows={4}
              autoFocus
              placeholder="e.g. Make this an MCQ on Newton's first law with 4 options, or introduce a worked example for refraction at 30°."
              className="w-full rounded-md border border-border bg-white p-2.5 text-sm text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
            <span className="mt-1 block text-[11px] text-fg-subtle">
              Leave blank to let Claude pick the next pedagogical beat (concept, example, question, recap, …).
            </span>
          </label>

          <div className="space-y-2">
            <label className="flex cursor-pointer select-none items-center gap-2 rounded-md border border-border bg-white px-3 py-2.5">
              <input
                type="checkbox"
                checked={includeImage}
                onChange={(e) => setIncludeImage(e.target.checked)}
                className="h-3.5 w-3.5 cursor-pointer accent-accent"
              />
              <ImageIcon size={14} className="text-fg-muted" />
              <span className="text-[12px] text-fg">
                Generate hero image with Gemini
                <span className="ml-1 text-fg-subtle">(adds ~5–15s)</span>
              </span>
            </label>

            <label className="flex cursor-pointer select-none items-center gap-2 rounded-md border border-border bg-white px-3 py-2.5">
              <input
                type="checkbox"
                checked={generateAudio}
                onChange={(e) => setGenerateAudio(e.target.checked)}
                className="h-3.5 w-3.5 cursor-pointer accent-accent"
              />
              <Mic size={14} className="text-fg-muted" />
              <span className="text-[12px] text-fg">
                Pre-record narration audio for every reveal step
                <span className="ml-1 text-fg-subtle">(adds ~6–15s)</span>
              </span>
            </label>
          </div>

          {error && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
          )}

          <div className="-mx-5 -mb-4 flex items-center justify-end gap-2 border-t border-border-subtle bg-bg-subtle/40 px-5 py-3">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={generate} disabled={!script}>
              <Sparkles size={14} /> Generate slide
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function BusyView({
  phase,
  elapsedMs,
  includeImage,
  generateAudio,
}: {
  phase: PhaseKey;
  elapsedMs: number;
  includeImage: boolean;
  generateAudio: boolean;
}) {
  const phases = PHASES.filter((p) => {
    if (p.key === 'illustrating' && !includeImage) return false;
    if (p.key === 'recording' && !generateAudio) return false;
    return true;
  });
  const seconds = (elapsedMs / 1000).toFixed(1);
  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center gap-3 rounded-md border border-accent/30 bg-accent-soft/40 px-4 py-3">
        <Loader2 size={18} className="animate-spin text-accent" />
        <div className="flex-1">
          <p className="text-sm font-medium text-fg">
            {phases.find((p) => p.key === phase)?.label ?? 'Working…'}
          </p>
          <p className="mt-0.5 text-[11px] text-fg-muted">
            This usually takes 10–30 seconds. The slide will be inserted automatically.
          </p>
        </div>
        <span className="font-mono text-[11px] text-fg-muted">{seconds}s</span>
      </div>

      <ol className="space-y-1.5">
        {phases.map((p) => {
          const currentIdx = phases.findIndex((x) => x.key === phase);
          const myIdx = phases.findIndex((x) => x.key === p.key);
          const isDone = myIdx < currentIdx;
          const isCurrent = p.key === phase;
          return (
            <li
              key={p.key}
              className="flex items-center gap-2 text-[12px] text-fg-muted"
            >
              <span
                className={`grid h-4 w-4 place-items-center rounded-full border ${
                  isDone
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : isCurrent
                      ? 'border-accent text-accent'
                      : 'border-border text-fg-subtle'
                }`}
              >
                {isDone ? '✓' : isCurrent ? <Loader2 size={9} className="animate-spin" /> : ''}
              </span>
              <span className={isCurrent ? 'text-fg' : ''}>{p.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

